import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { getPlan } from '../lib/planFeatures';
import { Card, CardContent } from '../components/ui/card';
import {
  Ruler, FolderOpen, Users, CreditCard, Clock, TrendUp,
  ChartBar, ChartPie, Lightning, Target, UserCircle, ArrowUpRight
} from '@phosphor-icons/react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#7367F0', '#28C76F', '#FF9F43', '#EA5455', '#00CFE8', '#FF6B6B', '#4ECDC4', '#FFE66D'];

function StatCard({ icon: Icon, label, value, sub, color = 'bg-primary/10 text-primary' }) {
  return (
    <Card className="shadow-card rounded-lg border-0">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${color}`}>
            <Icon size={20} weight="duotone" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-bold leading-none">{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, className = '' }) {
  return (
    <Card className={`shadow-card rounded-lg border-0 ${className}`}>
      <CardContent className="p-4 lg:p-5">
        <h3 className="text-sm font-semibold mb-4">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-md shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-semibold">{typeof p.value === 'number' && p.name?.toLowerCase().includes('revenue') ? `$${p.value.toFixed(2)}` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

// Shape Distribution — donut on the left, scrollable side-legend on the right.
// Replaces the previous inline `label` callback whose labels collided on small
// slices (~9+ shape types) into an unreadable cluster. Now every type shows
// up cleanly as `swatch · type · count · %` in a vertical list.
function ShapeDistributionDonut({ data }) {
  const total = data.reduce((s, d) => s + (d.count || 0), 0) || 1;
  return (
    <div className="flex flex-col sm:flex-row items-center gap-4" data-testid="shape-distribution-chart">
      <div className="w-full sm:w-1/2 h-[220px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="type"
              cx="50%"
              cy="50%"
              outerRadius={90}
              innerRadius={55}
              paddingAngle={2}
              stroke="none"
            >
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul
        className="w-full sm:flex-1 max-h-[220px] overflow-y-auto pr-1 space-y-1.5 text-xs"
        data-testid="shape-distribution-legend"
      >
        {data.map((d, i) => {
          const pct = ((d.count / total) * 100).toFixed(1);
          return (
            <li
              key={d.type}
              className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50 transition-colors"
              data-testid={`shape-legend-${d.type}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="truncate flex-1 text-foreground">{d.type}</span>
              <span className="text-muted-foreground tabular-nums">{d.count}</span>
              <span className="text-muted-foreground tabular-nums w-12 text-right">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function AnalyticsPage() {
  const { user, apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('measurements');
  const [data, setData] = useState({});

  const role = user?.role || 'regular_user';
  const isAdmin = role === 'super_admin';
  const isTenant = role === 'tenant_admin';
  // Plan gate — Basic plan tenants cannot view Analytics. Bounce to dashboard.
  // Super admins always have access. Placed after all hooks to honour Rules of Hooks.
  const isBlockedByPlan = !isAdmin && getPlan(user) === 'basic';

  const tabs = [
    { id: 'measurements', label: 'Measurements', icon: Ruler },
    { id: 'projects', label: 'Projects', icon: FolderOpen },
    ...(isAdmin || isTenant ? [{ id: 'team', label: 'Team', icon: Users }] : []),
    ...(isAdmin ? [{ id: 'billing', label: 'Billing', icon: CreditCard }] : []),
    ...(isAdmin || isTenant ? [{ id: 'activity', label: 'Activity', icon: Clock }] : []),
  ];

  useEffect(() => { if (!isBlockedByPlan) loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isBlockedByPlan) return <Navigate to="/dashboard" replace />;

  async function loadAll() {
    setLoading(true);
    try {
      const endpoints = ['measurements', 'projects'];
      if (isAdmin || isTenant) endpoints.push('team', 'activity');
      if (isAdmin) endpoints.push('billing');
      const results = await Promise.allSettled(
        endpoints.map(ep => apiCall('get', `/analytics/${ep}`))
      );
      const d = {};
      endpoints.forEach((ep, i) => {
        if (results[i].status === 'fulfilled') d[ep] = results[i].value.data;
      });
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const m = data.measurements || {};
  const p = data.projects || {};
  const t = data.team || {};
  const b = data.billing || {};
  const a = data.activity || {};

  return (
    <div className="p-4 lg:p-6 space-y-5 animate-fadeIn" data-testid="analytics-page">
      <div>
        <h4 className="text-lg lg:text-xl font-semibold" data-testid="analytics-heading">Analytics</h4>
        <p className="text-muted-foreground text-sm mt-0.5">
          {isAdmin ? 'Global platform insights' : isTenant ? 'Team performance & usage' : 'Your measurement insights'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-secondary/50 p-1 rounded-lg w-fit flex-wrap" data-testid="analytics-tabs">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${tab === t.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.id}`}
            >
              <Icon size={14} weight={tab === t.id ? 'fill' : 'regular'} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* MEASUREMENTS TAB */}
      {tab === 'measurements' && (
        <div className="space-y-5" data-testid="tab-content-measurements">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Ruler} label="Total Measurements" value={m.total_measurements || 0} color="bg-primary/10 text-primary" />
            <StatCard icon={Target} label="Total Area" value={`${((m.total_area || 0) / 1000).toFixed(1)}k m²`} color="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={TrendUp} label="Total Distance" value={`${(m.total_distance || 0).toFixed(0)} m`} color="bg-amber-500/10 text-amber-500" />
            <StatCard icon={ChartPie} label="Shape Types" value={(m.shape_distribution || []).length} color="bg-cyan-500/10 text-cyan-500" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Measurements Over Time">
              {(m.monthly_trend || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={m.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Measurements" fill="#7367F0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No trend data yet</p>}
            </ChartCard>
            <ChartCard title="Shape Distribution">
              {(m.shape_distribution || []).length > 0 ? (
                <ShapeDistributionDonut data={m.shape_distribution} />
              ) : <p className="text-xs text-muted-foreground text-center py-10">No shapes yet</p>}
            </ChartCard>
          </div>
          {(m.measurements_per_project || []).filter(p => (p?.count || 0) > 0).length > 0 && (
            <ChartCard title="Measurements Per Project (Top 10)">
              {(() => {
                // Hide projects with zero measurements — empty rows look like a
                // bug in the chart and add no information. The data is still
                // available via the Projects tab if needed.
                const rows = (m.measurements_per_project || []).filter(p => (p?.count || 0) > 0);
                return (
                  <ResponsiveContainer
                    width="100%"
                    height={Math.max(220, rows.length * 38 + 40)}
                  >
                    <BarChart
                      data={rows}
                      layout="vertical"
                      margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                      barCategoryGap="22%"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                      <YAxis
                        dataKey="name"
                        type="category"
                        tick={{ fontSize: 11 }}
                        width={160}
                        interval={0}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(v) => {
                          const s = (v ?? '').toString();
                          return s.length > 22 ? s.slice(0, 21) + '…' : s;
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Measurements" fill="#28C76F" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </ChartCard>
          )}
        </div>
      )}

      {/* PROJECTS TAB */}
      {tab === 'projects' && (
        <div className="space-y-5" data-testid="tab-content-projects">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <StatCard icon={FolderOpen} label="Total Projects" value={p.total_projects || 0} color="bg-primary/10 text-primary" />
            <StatCard icon={Users} label="Shared Projects" value={p.shared_projects || 0} color="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={FolderOpen} label="Private Projects" value={p.private_projects || 0} color="bg-amber-500/10 text-amber-500" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Projects Created Over Time">
              {(p.creation_trend || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={p.creation_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="count" name="Projects" stroke="#7367F0" strokeWidth={2} dot={{ fill: '#7367F0', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No data yet</p>}
            </ChartCard>
            <ChartCard title="Projects Per User">
              {(p.projects_per_user || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={p.projects_per_user}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Projects" fill="#FF9F43" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No data yet</p>}
            </ChartCard>
          </div>
        </div>
      )}

      {/* TEAM TAB */}
      {tab === 'team' && (isAdmin || isTenant) && (
        <div className="space-y-5" data-testid="tab-content-team">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Users} label="Total Users" value={t.total_users || 0} color="bg-primary/10 text-primary" />
            <StatCard icon={UserCircle} label="Active Users" value={t.active_users || 0} color="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={UserCircle} label="Inactive Users" value={t.inactive_users || 0} color="bg-red-500/10 text-red-500" />
            <StatCard icon={ArrowUpRight} label="Invite Accept Rate" value={`${t.invitations?.acceptance_rate || 0}%`} color="bg-amber-500/10 text-amber-500" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Role Distribution">
              {(t.role_distribution || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={t.role_distribution} dataKey="count" nameKey="role" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={3} label={({ role, count }) => `${role} (${count})`} labelLine={false}>
                      {(t.role_distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No data</p>}
            </ChartCard>
            <ChartCard title="Invitation Stats">
              <div className="space-y-3 pt-2">
                {[
                  { label: 'Total Sent', value: t.invitations?.total || 0, color: 'bg-primary' },
                  { label: 'Accepted', value: t.invitations?.accepted || 0, color: 'bg-emerald-500' },
                  { label: 'Pending', value: t.invitations?.pending || 0, color: 'bg-amber-500' },
                  { label: 'Expired', value: t.invitations?.expired || 0, color: 'bg-red-500' },
                ].map((s) => (
                  <div key={s.label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${s.color}`} />
                      <span className="text-xs text-muted-foreground">{s.label}</span>
                    </div>
                    <span className="text-sm font-semibold">{s.value}</span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>
          {(t.most_active_users || []).length > 0 && (
            <ChartCard title="Most Active Users">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="active-users-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-2 text-muted-foreground font-semibold">User</th>
                      <th className="text-left py-2 px-2 text-muted-foreground font-semibold">Role</th>
                      <th className="text-right py-2 px-2 text-muted-foreground font-semibold">Projects</th>
                      <th className="text-center py-2 px-2 text-muted-foreground font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.most_active_users.map((u, i) => (
                      <tr key={u.email || u.id || i} className="border-b border-border/50 last:border-0">
                        <td className="py-2 px-2">
                          <p className="font-medium">{u.name}</p>
                          <p className="text-muted-foreground text-[10px]">{u.email}</p>
                        </td>
                        <td className="py-2 px-2 capitalize text-muted-foreground">{u.role?.replace(/_/g, ' ')}</td>
                        <td className="py-2 px-2 text-right font-semibold">{u.projects}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block w-2 h-2 rounded-full ${u.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}
        </div>
      )}

      {/* BILLING TAB */}
      {tab === 'billing' && isAdmin && (
        <div className="space-y-5" data-testid="tab-content-billing">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={CreditCard} label="Total Revenue" value={`$${(b.total_revenue || 0).toFixed(0)}`} color="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={TrendUp} label="MRR" value={`$${(b.mrr || 0).toFixed(0)}`} color="bg-primary/10 text-primary" />
            <StatCard icon={ChartBar} label="Paid Transactions" value={b.paid_transactions || 0} color="bg-cyan-500/10 text-cyan-500" />
            <StatCard icon={Clock} label="Pending" value={b.pending_transactions || 0} color="bg-amber-500/10 text-amber-500" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Revenue Over Time">
              {(b.revenue_trend || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={b.revenue_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `$${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Revenue" fill="#28C76F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No revenue data yet</p>}
            </ChartCard>
            <ChartCard title="Plan Distribution">
              {(b.plan_distribution || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={b.plan_distribution} dataKey="count" nameKey="plan" cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={3} label={({ plan, count }) => `${plan} (${count})`} labelLine={false}>
                      {(b.plan_distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="bottom" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No plans yet</p>}
            </ChartCard>
          </div>
        </div>
      )}

      {/* ACTIVITY TAB */}
      {tab === 'activity' && (isAdmin || isTenant) && (
        <div className="space-y-5" data-testid="tab-content-activity">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Lightning} label="Total Actions (30d)" value={a.total_actions || 0} color="bg-primary/10 text-primary" />
            <StatCard icon={Clock} label="Peak Hour" value={a.peak_hour || 'N/A'} color="bg-amber-500/10 text-amber-500" />
            <StatCard icon={ChartBar} label="Peak Day" value={a.peak_day || 'N/A'} color="bg-emerald-500/10 text-emerald-500" />
            <StatCard icon={Target} label="Action Types" value={(a.top_actions || []).length} color="bg-cyan-500/10 text-cyan-500" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Daily Activity (Last 30 Days)">
              {(a.daily_trend || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={a.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="count" name="Actions" stroke="#7367F0" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No activity data</p>}
            </ChartCard>
            <ChartCard title="Activity by Day of Week">
              {(a.by_day || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={a.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Actions" fill="#FF9F43" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No data</p>}
            </ChartCard>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Activity by Hour">
              {(a.by_hour || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={a.by_hour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="hour" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" interval={2} />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Actions" fill="#00CFE8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No data</p>}
            </ChartCard>
            <ChartCard title="Top Actions">
              {(a.top_actions || []).length > 0 ? (
                <div className="space-y-2">
                  {a.top_actions.map((act, i) => {
                    const max = a.top_actions[0]?.count || 1;
                    return (
                      <div key={act.action || i} className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-28 shrink-0 truncate">{act.action}</span>
                        <div className="flex-1 h-5 bg-secondary/50 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(act.count / max) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                        </div>
                        <span className="text-xs font-semibold w-8 text-right">{act.count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-xs text-muted-foreground text-center py-10">No actions</p>}
            </ChartCard>
          </div>
        </div>
      )}
    </div>
  );
}
